import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import { wafFetch } from '@/lib/waf';
import type { JobApplication } from '@/types/job';

const schema = yup.object({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  email: yup.string().email('Please enter a valid email').required('Email is required'),
  phone: yup
    .string()
    .matches(/^[+]?[\d\s()-]{7,}$/, 'Please enter a valid phone number')
    .required('Phone number is required'),
  linkedInUrl: yup
    .string()
    .url('Please enter a valid URL')
    .optional()
    .transform((value) => (value === '' ? undefined : value)),
  coverLetter: yup.string().optional(),
});

interface ApplicationFormProps {
  jobTitle: string;
  department: string;
}

function ApplicationForm({ jobTitle, department }: ApplicationFormProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<JobApplication>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: JobApplication) => {
    const formData = new FormData();
    formData.append('job_title', jobTitle);
    if (department) formData.append('department', department);
    formData.append('first_name', data.firstName);
    formData.append('last_name', data.lastName);
    formData.append('email', data.email);
    formData.append('phone', data.phone);
    if (data.linkedInUrl) formData.append('linkedin_url', data.linkedInUrl);
    if (data.coverLetter) formData.append('cover_letter', data.coverLetter);
    if (data.resumeFile && data.resumeFile.length > 0) {
      formData.append('resume_file', data.resumeFile[0]);
    }

    try {
      const response = await wafFetch('/api/applications', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Server responded with ${response.status}`);
      }

      toast.success('Application submitted successfully! We\'ll be in touch soon.');
      reset();
    } catch (error) {
      console.error('Application submission failed:', error);
      toast.error('Something went wrong. Please try again.');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Name */}
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="firstName" className="label">
            First Name <span className="text-red-500">*</span>
          </label>
          <input
            id="firstName"
            type="text"
            placeholder="Jane"
            className="input-field"
            {...register('firstName')}
          />
          {errors.firstName && (
            <p className="mt-1 text-sm text-red-500">{errors.firstName.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="lastName" className="label">
            Last Name <span className="text-red-500">*</span>
          </label>
          <input
            id="lastName"
            type="text"
            placeholder="Doe"
            className="input-field"
            {...register('lastName')}
          />
          {errors.lastName && (
            <p className="mt-1 text-sm text-red-500">{errors.lastName.message}</p>
          )}
        </div>
      </div>

      {/* Email & Phone */}
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="email" className="label">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            id="email"
            type="email"
            placeholder="jane.doe@example.com"
            className="input-field"
            {...register('email')}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="phone" className="label">
            Phone <span className="text-red-500">*</span>
          </label>
          <input
            id="phone"
            type="tel"
            placeholder="+1 (555) 123-4567"
            className="input-field"
            {...register('phone')}
          />
          {errors.phone && (
            <p className="mt-1 text-sm text-red-500">{errors.phone.message}</p>
          )}
        </div>
      </div>

      {/* LinkedIn */}
      <div>
        <label htmlFor="linkedInUrl" className="label">
          LinkedIn Profile
        </label>
        <input
          id="linkedInUrl"
          type="url"
          placeholder="https://linkedin.com/in/janedoe"
          className="input-field"
          {...register('linkedInUrl')}
        />
        {errors.linkedInUrl && (
          <p className="mt-1 text-sm text-red-500">{errors.linkedInUrl.message}</p>
        )}
      </div>

      {/* Resume */}
      <div>
        <label htmlFor="resumeFile" className="label">
          Resume
        </label>
        <input
          id="resumeFile"
          type="file"
          accept=".pdf,.doc,.docx"
          className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 font-body text-sm text-heading file:mr-4 file:rounded-md file:border-0 file:bg-brand-purple-50 file:px-3 file:py-1.5 file:font-heading file:text-xs file:font-medium file:text-brand-purple hover:file:bg-brand-purple-100 focus:border-brand-purple focus:outline-none focus:ring-2 focus:ring-brand-purple-100"
          {...register('resumeFile')}
        />
        <p className="mt-1 text-xs text-muted">PDF, DOC, or DOCX (max 5MB)</p>
      </div>

      {/* Cover Letter */}
      <div>
        <label htmlFor="coverLetter" className="label">
          Cover Letter
        </label>
        <textarea
          id="coverLetter"
          rows={5}
          placeholder="Tell us why you're interested in this role and what makes you a great fit..."
          className="input-field resize-y"
          {...register('coverLetter')}
        />
      </div>

      {/* Submit */}
      <div className="pt-2">
        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
        >
          {isSubmitting ? (
            <span className="flex items-center gap-2">
              <svg className="h-4 w-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Submitting...
            </span>
          ) : (
            'Submit Application'
          )}
        </button>
      </div>
    </form>
  );
}

export default ApplicationForm;
